import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route } from 'react-router-dom';
import lazyLoader from '../../hoc/lazyLoader';
import { APP_URLS, PSR_REGISTER, PSR_REVIEW } from '../../settings/urls';

const RenderScenes = (props) => {
  const { journey, formStarted, formCompleted, history, handleResetForm, token, pathname } = props;

  const startComponent = lazyLoader(() => import('../../../scenes/01_start/Start'));
  const whatYouNeedComponent = lazyLoader(() => import('../../../scenes/02_what_you_need/WhatYouNeed'));
  const customerDetailsComponent = lazyLoader(() => import('../../../scenes/03_customer_details/CustomerDetails'));
  const yourDetailsComponent = lazyLoader(() => import('../../../scenes/04_your_details/YourDetails'));
  const servicesRequiredComponent = lazyLoader(() => import('../../../scenes/05_services_required/ServicesRequired'));
  const reasonsRequiredComponent = lazyLoader(() => import('../../../scenes/06_reasons_required/ReasonsRequired'));
  const securityCodewordComponent = lazyLoader(() => import('../../../scenes/07_security_codeword/SecurityCodeword'));
  const nomineeDetailsComponent = lazyLoader(() => import('../../../scenes/08_nominee_details/NomineeDetails'));
  const lifeChangesComponent = lazyLoader(() => import('../../../scenes/09_life_changes/LifeChanges'));
  const postHospitalComponent = lazyLoader(() => import('../../../scenes/10_post_hospital/PostHospital'));
  const youngAdultComponent = lazyLoader(() => import('../../../scenes/11_young_adult/YoungAdult'));
  const yourLanguageComponent = lazyLoader(() => import('../../../scenes/12_your_language/YourLanguage'));
  const summaryComponent = lazyLoader(() => import('../../../scenes/13_summary/Summary'));
  const confirmationComponent = lazyLoader(() => import('../../../scenes/14_confirmation/Confirmation'));
  const somethingWentWrongComponent = lazyLoader(() =>
    import('../../../scenes/15_something_went_wrong/SomethingWentWrong'),
  );
  const AutoEnrolPasswordComponent = lazyLoader(() =>
    import('../../../scenes/16_auto_enrol_password/AutoEnrolPassword'),
  );

  useEffect(() => {
    if (pathname === PSR_REGISTER || pathname === PSR_REVIEW) {
      handleResetForm();
    }
  }, [pathname, handleResetForm]);

  useEffect(() => {
    // Restart the journey if form has not started.
    if (!formStarted) {
      if (pathname.includes(PSR_REVIEW)) {
        history.push(`${PSR_REVIEW}${APP_URLS.START}`);
      } else {
        history.push(`${journey}${APP_URLS.START}`);
      }
    }
  }, [formStarted, pathname, history, journey]);

  useEffect(() => {
    return history.listen((location) => {
      if (history.action === 'POP') {
        // Redirect user to start page if form is submitted and browser's back button is clicked.
        if (formCompleted) {
          handleResetForm();
          history.push(`${journey}${APP_URLS.START}`);
        }
      }
    });
  }, [formCompleted, history, journey, handleResetForm]);

  useEffect(() => {
    // Redirect to start page after authentication from identity server.
    if (token && pathname.includes(APP_URLS.CALLBACK_RESPONSE)) {
      history.push(`${journey}${APP_URLS.START}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pathname]);

  const routeComponents = [
    { route: APP_URLS.START, component: startComponent },
    { route: APP_URLS.WHAT_YOU_NEED, component: whatYouNeedComponent },
    { route: APP_URLS.CUSTOMER_DETAILS, component: customerDetailsComponent },
    { route: APP_URLS.YOUR_DETAILS, component: yourDetailsComponent },
    { route: APP_URLS.SERVICES_REQUIRED, component: servicesRequiredComponent },
    { route: APP_URLS.REASONS_REQUIRED, component: reasonsRequiredComponent },
    { route: APP_URLS.SECURITY_CODEWORD, component: securityCodewordComponent },
    { route: APP_URLS.NOMINEE_DETAILS, component: nomineeDetailsComponent },
    { route: APP_URLS.TEMPORARY_LIFE_CHANGES, component: lifeChangesComponent },
    { route: APP_URLS.TEMPORARY_POST_HOSPITAL_RECOVERY, component: postHospitalComponent },
    { route: APP_URLS.TEMPORARY_YOUNG_ADULT_HOUSEHOLDER, component: youngAdultComponent },
    { route: APP_URLS.YOUR_LANGUAGE, component: yourLanguageComponent },
    { route: APP_URLS.SUMMARY, component: summaryComponent },
    { route: APP_URLS.CONFIRMATION, component: confirmationComponent },
    { route: APP_URLS.SOMETHING_WENT_WRONG, component: somethingWentWrongComponent },
    { route: APP_URLS.CREATE_LOGIN, component: AutoEnrolPasswordComponent },
  ];

  const renderComponent = (Component) => <Component {...props} />;

  return (
    <Switch>
      {routeComponents.map((current, index) => (
        <Route
          key={`${current.route}_${index}`}
          path={`${journey}${current.route}`}
          render={() => renderComponent(current.component)}
          exact
        />
      ))}
    </Switch>
  );
};

RenderScenes.propTypes = {
  formCompleted: PropTypes.bool,
  formStarted: PropTypes.bool,
  handleResetForm: PropTypes.func,
  history: PropTypes.object,
  isEditing: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  journey: PropTypes.string,
  pathname: PropTypes.string,
  token: PropTypes.string,
};

export default RenderScenes;
